import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, DateCalendar, Popper, TextField, Typography} from 'views/components';
import {CalendarToday, CalendarTomorrow, CalendarWeekend, CalendarNextWeek, AccessTimeOutlined as AccessTimeOutlinedIcon} from 'views/icons';
import {translate} from 'views/module';

import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';

import * as chrono from 'chrono-node';
import moment from 'moment';

const DatePickerQuickOptionCalendar = (props) => {
  const {
    anchorRef,
    onChange,
    open,
    onClose,
    value,
  } = props;

  const [inputValue, setInputValue] = React.useState('');
  const [selectedDate, setSelectedDate] = React.useState(() => {
    const parsed = value ? moment(value) : moment();
    return parsed.isValid() ? parsed : moment();
  });

  React.useEffect(() => {
    const parsed = value ? moment(value) : moment();
    if (!parsed?.isValid?.()) {
      return;
    }

    setSelectedDate(parsed);
  }, [value]);

  const getDayMoment = (dayOffset = 0) => {
    return moment().startOf('day').add(dayOffset, 'days');
  };

  const getNextWeekday = (weekday) => {
    const now = getDayMoment();
    const daysUntilWeekday = (weekday - now.day() + 7) % 7 || 7;
    return now.clone().add(daysUntilWeekday, 'days');
  };

  const getEndOfDay = (baseMoment) => {
    return baseMoment.clone().hour(23).minute(59).second(0).millisecond(0);
  };

  const formatQuickOptionDateLabel = (nextValue) => {
    return moment(nextValue).format('ddd, MMM D');
  };

  const quickSelectOptions = [
    {
      label: translate('tasks.Common.DatePickerQuickOptionCalendar.Today'),
      Icon: CalendarToday,
      getDate: () => getEndOfDay(getDayMoment()),
    },
    {
      label: translate('tasks.Common.DatePickerQuickOptionCalendar.Tomorrow'),
      Icon: CalendarTomorrow,
      getDate: () => getEndOfDay(getDayMoment(1)),
    },
    {
      label: translate('tasks.Common.DatePickerQuickOptionCalendar.ThisWeekend'),
      Icon: CalendarWeekend,
      getDate: () => getEndOfDay(getNextWeekday(6)),
    },
    {
      label: translate('tasks.Common.DatePickerQuickOptionCalendar.NextWeek'),
      Icon: CalendarNextWeek,
      getDate: () => getEndOfDay(getNextWeekday(1)),
    },
  ];

  const parseDateFromInput = (nextValue) => {
    if (!nextValue.trim()) {
      return null;
    }

    const referenceDate = moment().startOf('day').toDate();
    const parsedResult = chrono.parse(nextValue.trim(), referenceDate, {forwardDate: true})[0];

    if (!parsedResult) {
      return null;
    }

    const parsedDate = moment(parsedResult.start.date()).second(0).millisecond(0);
    return parsedDate.isValid() ? parsedDate : null;
  };

  const handleDateChange = (nextDate) => {
    const nextMoment = moment(nextDate);
    if (!nextMoment.isValid()) {
      return;
    }

    const parsedTime = parseDateFromInput(inputValue);
    const timeSource = parsedTime?.isValid?.() ? parsedTime : moment(selectedDate);

    nextMoment
        .hour(timeSource.hour())
        .minute(timeSource.minute())
        .second(0)
        .millisecond(0);

    setSelectedDate(nextMoment);
    onChange(nextMoment.utc().toISOString());
    onClose();
  };

  const handleQuickSelect = (optionDate) => {
    const nextMoment = moment(optionDate);
    if (!nextMoment.isValid()) {
      return;
    }

    setSelectedDate(nextMoment);
    onChange(nextMoment.utc().toISOString());
    onClose();
  };

  const handleInputChange = (nextValue, shouldCommit) => {
    setInputValue(nextValue);
    const parsedResult = parseDateFromInput(nextValue);

    if (parsedResult) {
      setSelectedDate(parsedResult);
      if (!shouldCommit) {
        return;
      }
      onChange(parsedResult.utc().toISOString());
      onClose();
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleInputChange(inputValue, true);
  };

  return (
    <>
      <Popper
        open={open}
        anchorEl={anchorRef?.current}
        placement='bottom-start'
        popperModifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}
        zIndex={1400}
        onClickAway={onClose}
      >
        <Box
          sx={{
            border: '1px solid #E8E4DE',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 8px 24px rgba(25, 25, 25, 0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              p: '8px',
              borderBottom: '1px solid #E8E4DE',
            }}
          >
            <TextField
              fullWidth
              variant='standard'
              size='small'
              placeholder={translate('tasks.Common.DatePickerQuickOptionCalendar.TypeDatePlaceholder')}
              value={inputValue}
              onChange={(nextValue) => handleInputChange(nextValue, false)}
              onKeyDown={handleInputKeyDown}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  px: 0.5,
                  py: 0.5,
                },
              }}
            />
            {quickSelectOptions.map((option) => {
              const optionDate = option.getDate();
              const IconComponent = option.Icon;
              const quickOptionDateLabel = formatQuickOptionDateLabel(optionDate);

              return (
                <Button
                  key={option.label}
                  variant='text'
                  onClick={() => handleQuickSelect(optionDate)}
                  sx={{
                    'justifyContent': 'flex-start',
                    'textTransform': 'none',
                    'borderRadius': '6px',
                    'color': '#1F1F1F',
                    'backgroundColor': 'transparent',
                    'px': 1,
                    '&:hover': {
                      'backgroundColor': '#F5F2EE',
                    },
                  }}
                >
                  <Box
                    sx={{
                      'width': '100%',
                      'display': 'flex',
                      'alignItems': 'center',
                      'justifyContent': 'space-between',
                      'gap': 1,
                    }}
                  >
                    <Box sx={{'display': 'inline-flex', 'alignItems': 'center', 'gap': 1}}>
                      <Box
                        component="img"
                        src={IconComponent}
                        sx={{width: 16, height: 16}}
                      />
                      <Typography sx={{fontSize: '13px', color: '#1F1F1F'}}>
                        {option.label}
                      </Typography>
                    </Box>
                    <Typography sx={{fontSize: '12px', color: '#78736D'}}>
                      {quickOptionDateLabel}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Box>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateCalendar
              value={selectedDate}
              onChange={handleDateChange}
            />
          </LocalizationProvider>
          <Box sx={{p: '8px', borderTop: '1px solid #E8E4DE'}}>
            <Box
              sx={{
                'width': '100%',
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'center',
                'border': '1px solid #E8E4DE',
                'backgroundColor': 'transparent',
                'borderRadius': '8px',
                'px': 1.5,
                'py': 0.5,
                'position': 'relative',
              }}
            >
              <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.75}}>
                <AccessTimeOutlinedIcon sx={{fontSize: 16, color: '#A8A49E'}} />
                <Typography sx={{fontSize: '14px', color: '#7A756E'}}>
                  {selectedDate?.isValid?.() ? selectedDate.format('h:mm A') : moment().format('h:mm A')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Popper>
    </>
  );
};

DatePickerQuickOptionCalendar.propTypes = {
  anchorRef: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  value: PropTypes.string,
};

export default React.memo(DatePickerQuickOptionCalendar);
